import { MonetaryInfo } from '@superbet-group/betting.lib.payments';
import {
    BetSlipPurchaseType,
    EventStatus,
    TicketType,
    SelectionType,
    SelectionSourceType,
    SelectionSourceScreen,
} from '@superbet-group/web.types';
import {
    isEmpty as _isEmpty,
    keyBy as _keyBy,
} from 'lodash';
import { Dispatch } from 'vuex';

import { logger } from '@app/logger';
import {
    findSelectionIndex,
    recreateSelection,
} from '@app/store/modules/ui/shared/betSlip/Helpers';
import ISportlikeEvent from '@core/models/base/ISportlikeEvent';
import { BetSlipData } from '@core/models/betSlip/IBetSlip';
import ISelection from '@core/models/betSlip/ISelection';
import {
    Payout,
    EMPTY_PAYOUT,
} from '@core/models/betSlip/Payout';
import SelectionChange from '@core/models/betSlip/SelectionChange';
import SportSelection from '@core/models/betSlip/SportSelection';
import { SerializedBetSlipData } from '@core/models/betSlip/serialization/ISerializedBetSlip';
import IBetSlipError, { BetSlipErrorFactory } from '@core/models/betSlip/validation/IBetSlipError';
import { WinCreditType } from '@core/models/bonus/WinCreditType';
import Odd from '@core/models/offer/Odd';
import OfferEvent from '@core/models/offer/OfferEvent';
import { BetSlipType } from '@core/models/shared/betSlip/betSlipEnums';
import User from '@core/models/user/User';
import { validationResultService } from '@models/betSlip/validation/IValidationResult';
import { getPaymentBonusType } from '@shared/betSlip/helpers/betSlipBonusHelper';
import {
    RecreatableTicket,
    RecreatableTicketEventOdd,
} from '@shared/tickets/RecreatableTicket';
import {
    showBetBuilderConflictModal,
    checkIfBetBuilderIsReplaced,
} from '@sportOfferFederation/bet-builder/utils/conflicts';
import { transformSportBetSlipToTicket } from '@sportOfferFederation/betSlip/mappers/transformSportBetslipToTicketDTO';
import { createMatchSubscriptions } from '@store/modules/ui/betSlip/helpers/matchSubscriptions';
import {
    validateSportBetSlip,
    validateBonusBetSlip,
    validateSportSelections,
} from '@store/modules/ui/betSlip/helpers/validation';

import { MUTATION_TYPE } from './mutations';
import {
    SelectionAddResult,
    BetSlipContext,
    MatchSubscription,
    AllBetSlipErrors,
    BetSlipSelectionAddMode,
} from './types';
import { SseUpdate } from '@core/services/offer/_private/sse/types';
import { recreateOddFromTicketOdd } from './helpers/odds';
import modalCodes from '../shared/modal/modalCodes';
import {
    getParamWithPrefix,
    ParamTypes,
    Prefix,
    SelectionSourceScreenMap,
} from '@src/app/utils/analyticsScreenNames';
import { ALL_MARKETS_GROUP_ID } from '@modules/eventGrid/components/EventRow/marketGroupHelpers';
import {
    checkIfBoostedOddConflictOccurs,
    showBoostedOddConflictModal,
    ReplacementType,
} from '@sportOfferFederation/boostedOdds/utils/conflicts';
import { BonusErrorType } from '@superbet-group/betting.lib.validation';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import {
    BetslipResult,
    Status,
} from '@superbet-group/product.analytics';
import BetSlipOdd from '@core/models/base/BetSlipOdd';
import { createBetSlipSelectionsHelpers } from './betslipSelectionsHelpers';
import { areMarketsAllowed } from './helpers/allowances';
import { isMobileLayout } from '@superbet-group/web.lib.vue-utilities';

type Action = (context: BetSlipContext, payload: any) => any;

export function createActions({
    maxSystemsCount,
    subscribeToEvent,
    getEvents,
    getPayout,
    getDefaultStake,
    getMatch,
    getUser,
    roundToTwoDecimals,
    getMultiMarketSelectEnabledFlag,
}: {
    maxSystemsCount: number,
    subscribeToEvent: (eventId: OfferEvent['id'], callback: (match: SseUpdate<OfferEvent>) => void) => MatchSubscription;
    getEvents: (eventIds: OfferEvent['id'][]) => Promise<OfferEvent[]>;
    getPayout: (betSlipData: BetSlipData, monetaryInfo: MonetaryInfo) => Promise<Payout>;
    getDefaultStake: () => number;
    getMatch: (dispatch: Dispatch, rootGetters: any, id: OfferEvent['id']) => Promise<OfferEvent>;
    getUser: (rootGetters: any) => User | null;
    roundToTwoDecimals: (value: number) => string;
    getMultiMarketSelectEnabledFlag: () => Promise<boolean>,
}): Record<string, Action> {
    const helpers = createBetSlipSelectionsHelpers(maxSystemsCount, areMarketsAllowed);

    const matchSubscriptions = createMatchSubscriptions(
        subscribeToEvent,
        getEvents,
    );

    function getMatchesFromServer(selections: ISelection[]) {
        const matchIds = selections.map((s) => s.getEventId());
        return getEvents(matchIds);
    }

    function validateBetSlip(
        context: BetSlipContext,
        selections: ISelection[],
        isSubmit: boolean,
        isOnline: boolean,
    ): AllBetSlipErrors {
        const { state, getters, rootState, rootGetters } = context;

        const betSlip = {
            selections,
            purchaseType: state.purchaseType,
            stake: getters.stake.gross,
            stakePerCombination: getters.stake.stakePerCombination,
            selectedSystems: getters.selectedSystems,
            type: getters.type,
            payout: getters.payout,
            totalCoefficient: getters.totalCoefficient,
            combinationsOddSum: getters.combinationsOddSum,
        };

        const rounding = {
            toTwoDecimals: roundToTwoDecimals,
        };

        const user = getUser(rootGetters);
        const userBalance = user?.balance?.realMoney.total;
        const tempAccount = user ? user.isTempAccount : false;
        const activeSportBonus = rootState.ui?.user?.bonuses?.activeBonusEligibility;
        const sportValidation = validateSportBetSlip(betSlip, activeSportBonus, isSubmit, rounding, tempAccount, userBalance, isOnline);
        const bonusValidation = activeSportBonus ? validateBonusBetSlip(betSlip, activeSportBonus, isOnline, userBalance) : null;
        return {
            sportValidation,
            bonusValidation,
        };
    }

    function setChangesIfNeeded({ commit }: BetSlipContext, newSelections: ISelection[], matches: OfferEvent[]) {
        newSelections.forEach((s) => {
            if (!s.isSportType()) {
                return;
            }

            const match = matches.find(({ id }) => id === s.getEventId());

            if (!match) {
                return;
            }

            const selectionOddId = s.getOddId();
            const oddKey = (s as SportSelection).getOddUniqueId();

            if (!oddKey) {
                return;
            }

            const odd = selectionOddId ? match.getOddsMap()[oddKey] : null;

            if (s.getOddValue() === odd?.value) {
                return;
            }

            const newSelection = SportSelection.createItem(match, odd);
            newSelection.isFixed = s.isFixed;
            const selectionChange = new SelectionChange({
                newSelection,
                selection: s,
            });
            commit(MUTATION_TYPE.ADD_SELECTION_CHANGE, selectionChange);
        });
    }

    function updateSelections(context: BetSlipContext, selections: ISelection[], matches: OfferEvent[]) {
        return Promise.all(selections.map((s) => {
            if (s.isSportType() && matches.find((m) => m.id === s.getEventId())) {
                const match = matches.find((m) => m.id === s.getEventId())!;
                const odd = s.odd ? match.getOddsMap()[(s.odd as Odd).uniqueId] : null;
                const updatedSelection = SportSelection.createItem(match, odd, s.sourceType);

                // Keep original odd for BB, it will get updated via SSE, and these odds don't show up
                // in BB markets
                if (s.sourceType === SelectionSourceType.BetBuilder && odd == null && s.odd != null) {
                    updatedSelection.odd = s.odd;
                }

                context.dispatch('updateSelection', { selection: updatedSelection });
                return Promise.resolve(updatedSelection);
            }
            return Promise.resolve(s);
        }));
    }

    async function createSelection(context: BetSlipContext, payload: CreateSelectionData): Promise<SportSelection> {
        const {
            eventId,
            oddId,
            specialBetValue,
            uuid,
            originalOdd,
            originalMarketId,
            sourceScreen,
            isFixed,
            index,
            eventComponents,
        } = payload;
        const event = await getMatch(context.dispatch, context.rootGetters, eventId);
        const bbOddUuids = eventComponents?.map(({ oddComponent }) => oddComponent.oddUuid!);
        const getBetBuilderBetSlipOdd: (oddUuid: BetSlipOdd['uuid']) => Promise<BetSlipOdd | undefined> = (oddUuid: BetSlipOdd['uuid']) => context.dispatch('data/betBuilder/getCreatedOdd', {
            eventId,
            oddUuid,
            event,
            bbOddUuids,
        }, { root: true });
        const odd = event.getOdd({ uuid, id: oddId, specialBetValue })
                ?? await recreateOddFromTicketOdd(
                    originalOdd ?? { oddUuid: uuid, sourceType: SelectionSourceType.BetBuilder } as unknown as RecreatableTicketEventOdd,
                    originalMarketId,
                    getBetBuilderBetSlipOdd,
                );

        const selection = SportSelection.createItem(event, odd, originalOdd?.sourceType, sourceScreen ?? originalOdd?.sourceScreen, index);

        if (isFixed) {
            selection.isFixed = true;
        }

        return selection;
    }

    async function createSelectionFromRecreatableEvent(
        context: BetSlipContext,
        payload: {
            event: RecreatableTicket['events'][0];
            sourceScreen?: SelectionSourceScreen;
        },
    ): Promise<ToggleSelectionResult> {
        const { event, sourceScreen } = payload;

        if (event.status !== EventStatus.active) {
            return { isSuccess: false };
        }

        const eventId = parseInt(event.eventId, 10);
        const oddId = parseInt(event.odd.oddId!, 10);
        const specialValue = event.odd.specialValue ? event.odd.specialValue : undefined;
        const uuid = event.odd.oddUuid;

        const selection = await createSelection(context, {
            oddId,
            eventId,
            originalOdd: event.odd,
            originalMarketId: event?.market?.marketId,
            uuid,
            specialBetValue: specialValue,
            sourceScreen: sourceScreen ?? event.odd.sourceScreen,
            eventComponents: event.eventComponents,
        });

        selection.isFixed = !!event.odd.isFix;

        // `shouldSubscribe` was originally set to`false` and has been updated to `true` based on BMB-1007 & SB-58
        const result: ToggleSelectionResult = await context.dispatch('_toggleSelection', { selection, shouldSubscribe: true });

        if (result.addResult !== SelectionAddResult.removed) {
            context.dispatch('sendBetslipAddResultAnalytic', { selection, isSuccess: result.isSuccess, sourceScreen });
        }

        return result;
    }

    async function createSelections(
        context: BetSlipContext,
        payload: {
            ticket: RecreatableTicket;
            sourceScreen?: SelectionSourceScreen;
        },
    ): Promise<void> {
        const { ticket, sourceScreen } = payload;

        for (let i = 0; i < ticket.events.length; i += 1) {
            const event = ticket.events[i];

            // eslint-disable-next-line no-await-in-loop
            await createSelectionFromRecreatableEvent(context, { event, sourceScreen });
        }
    }

    function splitBetsFromUrl(bets: string[] | string): BetUrlParams[] {
        if (typeof bets === 'string') {
            // eslint-disable-next-line no-param-reassign
            bets = [bets];
        }
        return bets.map((bet) => {
            const [matchId, oddId, osbv, fix, ouuid] = bet.split(',');
            const mi = Number.parseInt(matchId);
            const oi = Number.parseInt(oddId);
            if (isNaN(mi) || isNaN(oi)) {
                throw Error('Wrong query parameters for selection');
            }
            return {
                mi,
                oi,
                osbv,
                fix,
                ouuid,
            };
        });
    }

    function splitCopiedTicketsFromUrl(copied: string[] | string): CopiedTicketUrlParams[] {
        if (!copied) {
            return [];
        }

        const source = typeof copied === 'string' ? [copied] : copied;

        return source.map((ticket) => {
            const [ticketId, userId, ticketCopySource, videoId] = ticket.split(',');
            return {
                id: ticketId,
                userId: userId,
                ticketCopySource,
                videoId,
            };
        });
    }

    function splitCopiedSelectionsFromUrl(copied: string[] | string): CopiedSelectionUrlParams[] {
        if (!copied) {
            return [];
        }

        const source = typeof copied === 'string' ? [copied] : copied;

        return source.map((ticket) => {
            const [
                analysisId,
                analysisSource,
                userId,
                ticketId,
                eventId,
                oddUuid,
            ] = ticket.split(',');
            return {
                analysisId: analysisId || undefined,
                analysisSource: analysisSource || undefined,
                userId: userId,
                ticketId: ticketId,
                event: eventId ? { eventId } : undefined,
                odd: oddUuid ? { oddUuid } : undefined,
            };
        });
    }

    /**
     * Payout needs to be adjusted when FreeBet bonus is used.
     * FreeBet bonus is bonus before money, and with this bonus money needs to be reduced from payout.
     *
     * On PL due to tax, if coefficient is smaller than 1.14 win will be negative. Then we need to show 0 instead of negative win.
     */
    function adjustNetWinPayout(payout: number, stake: number, bonus: number): number {
        let usedBonusMoney = 0;
        if (bonus >= stake) {
            usedBonusMoney = stake;
        } else {
            usedBonusMoney = bonus;
        }

        return Math.max(0, payout - usedBonusMoney);
    }

    /**
     *  Mode `append` differs from original add-from-url flow:
     *   - Appending selections to the existing BetSlip index.
     *   - Keeping the user-inputted stake, not one specified in Directus link.
     *   - Adding selections directly to the BetSlip (no preview modal).
     */
    function appendToBetSlip(bets: BetUrlParams[], context: BetSlipContext) {
        for (const bet of bets) {
            const {
                mi: eventId,
                ouuid: uuid, // Use uuid, as odd id can lead to unexpected selections in non-final markets.
                fix,
            } = bet;

            if (uuid && eventId) {
                context.dispatch('toggleSelection', { eventId, uuid, isFixed: fix === '1' });
            } else {
                logger.error(new Error('Odd uuid and eventId are required when appending the Odds to BetSlip!'));
            }
        }
    }

    const actions: Record<string, Action> = {
        async addSelectionsFromUrl(context) {
            const { dispatch, rootGetters } = context;
            const { query } = rootGetters['navigation/route'];
            const {
                stake,
                'bets[]': betsString,
                ct: copiedString,
                'cs[]': copiedSelectionsString,
                mode,
                type,
                systems,
            } = query;
            try {
                const newBets = splitBetsFromUrl((betsString || []) as (string[] | string));
                const copiedTickets = splitCopiedTicketsFromUrl(copiedString as string);
                const copiedSelections = splitCopiedSelectionsFromUrl(copiedSelectionsString as string | string[]);

                const matches = newBets.map((bet) => Number(bet.mi));
                const events = await getEvents(matches);
                dispatch('data/sportOffer/setEvents', { events }, { root: true });

                if (mode === BetSlipSelectionAddMode.Append) {
                    appendToBetSlip(newBets, context);
                    return;
                }

                const newStake = Number.parseFloat(stake as string);
                if (!isNaN(newStake)) {
                    dispatch('setStake', Number(stake));
                }

                for (const bet of newBets) {
                    const { mi: eventId, oi: oddId, ouuid: uuid, osbv: specialBetValue, fix } = bet;
                    dispatch('toggleSelection', { eventId, oddId, uuid, specialBetValue, isFixed: fix === '1' });
                }

                if (type) {
                    if (type === 'simple') {
                        dispatch('setType', BetSlipType.simple);
                    } else if (type === 'system') {
                        dispatch('setType', BetSlipType.system);
                    }
                }

                if (type === 'system' && systems) {
                    const parsedSystems = systems.toString().split(',').map((s) => parseInt(s, 10));
                    dispatch('setSystems', { systems: parsedSystems });
                }

                if (copiedTickets?.length) {
                    dispatch('data/tickets/setCopiedTickets', copiedTickets, { root: true });
                }

                if (copiedSelections?.length) {
                    await Promise.all(copiedSelections.map((s) => dispatch(
                        'data/tickets/addCopiedSelection',
                        s,
                        { root: true },
                    )));
                }

                if (isMobileLayout()) {
                    dispatch('ui/modal/setModal', {
                        code: modalCodes.mobileBetSlip,
                    }, { root: true });
                }
            } catch (e) {
                logger.error(new Error(`Bets should be in format matchId,oddId,specialBetValue,fix,oddUuid: ${JSON.stringify(e)}`));
            }
        },

        async toggleSelection(
            context: BetSlipContext,
            payload: BetSlipSelection,
        ) {
            if (payload.uuid) {
                context.commit(MUTATION_TYPE.SET_IS_SUBMITTING_ODD, { uuid: payload.uuid, value: true });
            }
            const selection = await createSelection(context, payload);
            const result = await context.dispatch('_toggleSelection', { selection });

            if (result.addResult !== SelectionAddResult.removed) {
                context.dispatch('sendBetslipAddResultAnalytic',
                    {
                        selection,
                        isSuccess: result.isSuccess,
                        sourceScreen: payload.sourceScreen,
                        isSearchActive: payload.isSearchActive,
                        isFavourited: payload.isFavourited,
                    });
            }

            if (payload.uuid) {
                context.commit(MUTATION_TYPE.SET_IS_SUBMITTING_ODD, { uuid: payload.uuid, value: false });
            }
        },

        // eslint-disable-next-line sonarjs/cognitive-complexity
        async _toggleSelection(context, {
            selection,
            shouldSubscribe = true,
            autoReplaceDuplicate,
        }: {
            selection: ISelection;
            shouldSubscribe?: boolean;
            autoReplaceDuplicate?: boolean;
        }): Promise<ToggleSelectionResult> {
            const { state, dispatch, getters, commit } = context;

            const conflict = checkIfBetBuilderIsReplaced(selection, getters.selections);
            const boostedOddConflict = await checkIfBoostedOddConflictOccurs(selection, getters.selections);

            if (boostedOddConflict) {
                const result = await showBoostedOddConflictModal(context, {
                    selections: getters.selections,
                    newSelection: selection,
                    isNewOddBoosted: boostedOddConflict === ReplacementType.NewBoosted,
                });

                if (!result.isAccepted) {
                    return { isSuccess: false };
                }
            } else if (conflict.isBetBuilderIdentical) {
                // This is not a real conflict at all. No analytics necessary, just cancel and clear the bet builder
                return {
                    isSuccess: true,
                };
            } else if (conflict.isBetBuilderReplaced) {
                if (!autoReplaceDuplicate) {
                    const betBuilderAnalytics = getBetBuilderConflictAnalytics(context, selection);

                    betBuilderAnalytics.conflictOccurred();

                    const result = await showBetBuilderConflictModal(context, {
                        selection: conflict.selection!,
                    });

                    if (!result.isAccepted) {
                        betBuilderAnalytics.conflictCancelled();

                        return { isSuccess: false };
                    }

                    betBuilderAnalytics.conflictReplaced();
                }

                const duplicateSelection = getters.selections.find((s) => s.getEventId() === selection.getEventId());
                await context.dispatch('removeSelection', { selection: duplicateSelection });
            }

            let addResult: SelectionAddResult;

            if (boostedOddConflict != null) {
                await context.dispatch('clearSelections');
            }

            addResult = await context.dispatch('toggleSelectionBase', { selection });
            if (addResult === SelectionAddResult.added && shouldSubscribe) {
                await context.dispatch('_subscribeSelectionForUpdates', selection);
            } else if (addResult === SelectionAddResult.removed) {
                await context.dispatch('_unsubscribeSelectionFromUpdates', selection);
            }

            // Special logic - for single selection boosted odds,
            // if we got this far we ONLY want that one odd.
            const updatedSelections = boostedOddConflict != null
                ? [selection]
                : [...getters.selections];

            const result = validateBetSlip(
                context,
                updatedSelections,
                false,
                context.getters.isOnline,
            );

            const selectionErrors = await validateSportSelections({
                purchaseType: state.purchaseType,
                betSlipType: getters.type,
                selections: updatedSelections,
                stake: getters.stake.gross,
                isSubmit: false,
            });

            const error = validationResultService
                .getFirstErrorWithAddSelectionRejection({
                    betSlipError: result.sportValidation[0],
                    selectionErrors,
                }, `${selection.getEventId()}`);
            if (error) {
                commit(MUTATION_TYPE.SET_SELECTION_ADD_ERROR, error);
            }

            dispatch('calculatePayout');
            dispatch('checkBetSlipValidation');
            return { isSuccess: true, addResult: addResult! };
        },

        async removeSelection(context, { selection, trackAnalytics }: { selection: ISelection, trackAnalytics?: boolean }) {
            const { state, commit, getters, dispatch } = context;
            await context.dispatch('_removeSelection', { selection });
            dispatch('data/sportOffer/removeSubscription', selection.getEventId(), { root: true });
            dispatch('_unsubscribeSelectionFromUpdates', selection);
            if (state.changes[selection.getEventId()]) {
                commit(MUTATION_TYPE.REMOVE_SELECTION_CHANGE, selection.getEventId());
            }
            dispatch('calculatePayout');
            dispatch('checkBetSlipValidation');

            if (trackAnalytics && selection.sourceType === SelectionSourceType.BetBuilder) {
                recordBetBuilderBetRemoved(context, selection);
            }

            dispatch('data/tickets/removeCopiedSelection', selection.getEventId(), { root: true });

            const selectionIds = getters.selections.map((selection) => selection.getEventId());
            dispatch('data/tickets/checkAndRemoveCopiedTicket', selectionIds, { root: true });
        },

        async refreshSelectionsStatus(context: BetSlipContext) {
            const selections = context.getters.selections;
            const eventIds = context.getters.selections.map((selection) => selection.getEventId());

            matchSubscriptions.clear();
            context.commit(MUTATION_TYPE.CLEAR_SELECTION_CHANGES);

            const matches = await getMatchesFromServer(selections);

            // Remove all events which are either so old that they're no longer
            // in the offer, _or_ have already finished
            const matchLookup = new Map(matches.map((match) => [match.id, match]));
            const removedSelections = selections.filter((selection) =>
                !matchLookup.has(selection.getEventId())
                    || (matchLookup.get(selection.getEventId())!.isFinished() && !matchLookup.get(selection.getEventId())!.isStopped()),
            );

            for (const selection of removedSelections) {
                // eslint-disable-next-line no-await-in-loop
                await context.dispatch('removeSelection', { selection });
            }

            // Depending on player preferences, either patch selections or
            // ask them to confirm the changes
            if (context.getters.autoApplyChanges) {
                await updateSelections(context, selections, matches);
            } else {
                setChangesIfNeeded(context, selections, matches);
            }

            matchSubscriptions.addAll(context, eventIds);

            // Re-run validations with the latest data
            context.dispatch('calculatePayout');
            context.dispatch('checkBetSlipValidation');
        },

        async updateSingleSelection(context, selection: ISelection) {
            await context.dispatch('updateSelection', { selection });
            await context.dispatch('calculatePayout');
            await context.dispatch('checkBetSlipValidation');
        },

        async acceptChange(
            context,
            change: SelectionChange,
        ) {
            const { commit, dispatch } = context;
            commit(MUTATION_TYPE.REMOVE_SELECTION_CHANGE, change.selection.getEventId());
            await context.dispatch('updateSelection', { selection: change.newSelection });
            dispatch('calculatePayout');
            dispatch('checkBetSlipValidation');
        },

        async declineChange(
            context,
            change: SelectionChange,
        ) {
            const { commit, dispatch } = context;
            commit(MUTATION_TYPE.REMOVE_SELECTION_CHANGE, change.selection.getEventId());
            await context.dispatch('_removeSelection', { selection: change.newSelection });
            dispatch('calculatePayout');
            dispatch('checkBetSlipValidation');
        },

        toggleSystemSelected({ getters, dispatch }, system: number) {
            if (getters.selectedSystems.find((s) => s.getMinNumber() === system && s.isSelected)) {
                dispatch('removeSelectedSystem', system);
            } else {
                dispatch('addSelectedSystem', system);
            }
        },

        addSelectedSystem(context, system: number) {
            if (system <= context.state.selections.filter((s) => !s.isFixed).length) {
                context.commit(MUTATION_TYPE.ADD_SELECTED_SYSTEM, system);
            }
            context.dispatch('calculatePayout');
            context.dispatch('checkBetSlipValidation');
        },

        removeSelectedSystem(context, system: number) {
            context.commit(MUTATION_TYPE.REMOVE_SELECTED_SYSTEM, system);
            context.dispatch('calculatePayout');
            context.dispatch('checkBetSlipValidation');
        },

        toggleAutoUpdateChanges({ commit, state }) {
            commit(MUTATION_TYPE.SET_AUTO_UPDATE_CHANGES, !state.autoUpdateChanges);
        },

        toggleSelectionIsFixed(context, selection) {
            context.dispatch('setSelectionIsFixed', { selection, isFixed: !selection.isFixed });
            context.dispatch('calculatePayout');
            context.dispatch('checkBetSlipValidation');
        },

        async clear(
            context,
        ) {
            const { commit, getters, dispatch } = context;
            const selections = [...getters.selections];
            // Selection removal occurs in parallel
            await Promise.all(selections.map((selection) => dispatch('removeSelection', { selection, trackAnalytics: true })));

            commit(MUTATION_TYPE.SET_IS_BONUS_SELECTED, true);

            dispatch('calculatePayout');
            dispatch('checkBetSlipValidation');
            dispatch('data/tickets/setCopiedTickets', [], { root: true });
        },

        setType(context, type: BetSlipType) {
            context.commit(MUTATION_TYPE.SET_TYPE, type);
            if (type === BetSlipType.system) {
                helpers.limitMaxSystems(context);
            }
            context.dispatch('calculatePayout');
            context.dispatch('checkBetSlipValidation');
        },
        setStake(context, stake: number) {
            context.commit(MUTATION_TYPE.SET_STAKE, stake);
            context.dispatch('calculatePayout');
            context.dispatch('checkBetSlipValidation');
        },
        validateBetSlipErrors(context, isSubmit = false) {
            const result = validateBetSlip(context, context.getters.selections, isSubmit, context.getters.isOnline);

            context.commit(MUTATION_TYPE.CLEAR_BETSLIP_ERRORS);
            if (result.sportValidation.length > 0) {
                context.commit(MUTATION_TYPE.SET_BETSLIP_ERRORS, result);
            }
            if (result.bonusValidation?.length && context.getters.selections?.length > 0) {
                context.commit(MUTATION_TYPE.SET_BONUS_ELIGIBLITY_ERROR, result.bonusValidation[0]);
            } else {
                context.commit(MUTATION_TYPE.CLEAR_BONUS_ELIGIBLITY_ERROR);
            }
        },
        async validateSelections({ commit, getters, state }, isSubmit = false) {
            commit(MUTATION_TYPE.CLEAR_ERRORS);
            commit(MUTATION_TYPE.SET_SELECTION_ADD_ERROR, null);

            if (getters.selections.length === 0) {
                return;
            }

            const selectionErrors = await validateSportSelections({
                purchaseType: state.purchaseType,
                betSlipType: getters.type,
                selections: getters.selections,
                stake: getters.stake.gross,
                isSubmit,
            });
            if (isSubmit && !_isEmpty(selectionErrors)) {
                commit(MUTATION_TYPE.ADD_ERROR, BetSlipErrorFactory.createErrorsInSelections());
            }

            commit(MUTATION_TYPE.SET_SELECTION_ERRORS, selectionErrors);
        },
        async calculatePayout({ state, getters, commit, rootState }) {
            if (getters.selectionsWithOdd.length === 0) {
                commit(MUTATION_TYPE.SET_PAYOUT, EMPTY_PAYOUT);
                return;
            }

            if (getters.type === BetSlipType.system && getters.selectedSystems.length === 0) {
                commit(MUTATION_TYPE.SET_PAYOUT, EMPTY_PAYOUT);
                return;
            }

            if (!state.payoutLocked && getters.rawStake) {
                const betSlipData = {
                    selections: getters.selectionsWithOdd,
                    stake: getters.stake.gross,
                    purchaseType: state.purchaseType,
                    selectedSystems: getters.selectedSystems,
                    type: getters.type,
                };
                const payout = await getPayout(betSlipData, {
                    userBalance: getters.userBalance,
                    sportBonusBalance: getters.sportBonusBalance,
                });
                const ticketBonus = rootState.ui.user.bonuses.activeBonusEligibility;

                if (ticketBonus?.balance
                    && ticketBonus.winType === WinCreditType.Net
                    && betSlipData.purchaseType === BetSlipPurchaseType.online
                    && getters.isBonusSelected) {
                    payout.final = adjustNetWinPayout(payout.final, getters.stake.gross, ticketBonus.balance);
                }
                commit(MUTATION_TYPE.SET_PAYOUT, payout);
            }
        },
        setPurchaseType(context, type: BetSlipPurchaseType) {
            context.commit(MUTATION_TYPE.SET_PURCHASE_TYPE, type);
            context.dispatch('calculatePayout');
            context.dispatch('checkBetSlipValidation');
        },
        setDefaultPurchaseType(context, type: BetSlipPurchaseType) {
            context.commit(MUTATION_TYPE.SET_PURCHASE_TYPE, type);
            context.dispatch('calculatePayout');
            context.dispatch('checkBetSlipValidation');
        },
        setDefaultStake(context) {
            const { dispatch, state } = context;
            const defaultStake = getDefaultStake();
            if (state.stake == null) {
                dispatch('setStake', defaultStake);
            }
            dispatch('calculatePayout');
            dispatch('checkBetSlipValidation');
        },
        // deserialize
        // eslint-disable-next-line sonarjs/cognitive-complexity
        getSelectionsFromStorage: async function (context, serializedInstanceStates: SerializedBetSlipData[]) {
            const { commit, dispatch, getters } = context;

            commit(MUTATION_TYPE.SET_PAYOUT_IS_LOCKED, true);
            const betSlip = serializedInstanceStates[0];
            context.dispatch('setStake', betSlip.stake);
            context.dispatch('setType', betSlip.type);
            context.dispatch('setSystems', { systems: betSlip.selectedSystems });
            commit(MUTATION_TYPE.SET_PAYOUT_IS_LOCKED, false);

            const allSelections = betSlip.selections;
            const matches = await getEvents(allSelections.map((s) => s.eventId!));

            const matchMap = _keyBy(matches, 'id');

            const changes: Record<OfferEvent['id'], SelectionChange> = {};
            const selectionsDrafts = allSelections
                .filter((s) => {
                    return matchMap[s.eventId!];
                })
                .map((s) => {
                    const selection = recreateSelection({
                        event: matchMap[s.eventId!],
                        oddId: s.oddId,
                        stashedOdd: s.stashedOdd,
                        specialBetValue: s.specialBetValue,
                        sourceType: s.sourceType,
                        sourceScreen: s.sourceScreen,
                    });
                    selection.isFixed = s.isFixed;
                    if (!getters.autoApplyChanges && s.oddValue !== selection.getOddValue()) {
                        const change = new SelectionChange({
                            selection,
                            newSelection: selection,
                            oldValue: s.oddValue || undefined,
                        });
                        changes[s.eventId!] = change;
                    }
                    return selection;
                });
            commit(MUTATION_TYPE.SET_CHANGES, changes);

            const addedSelections: SportSelection[] = [];

            selectionsDrafts.forEach((s) => {
                if (addedSelections.some((added) => added.isEqual(s))) {
                    return;
                }
                addedSelections.push(s);
            });

            addedSelections.forEach((added) => {
                dispatch('_toggleSelection', {
                    selection: added,
                    shouldSubscribe: true,
                });
            });

            if (!context.getters.isEmpty) {
                dispatch('checkBetSlipValidation');
            }
            dispatch('calculatePayout');
        },

        setWarning({ commit }, warning: IBetSlipError | null) {
            commit(MUTATION_TYPE.SET_WARNING, warning);
        },

        setBonusWarning({ commit }, warning: IBetSlipError | null) {
            commit(MUTATION_TYPE.SET_BONUS_WARNING, warning);
        },

        setIsBonusSelected({ commit, dispatch }, value: boolean) {
            commit(MUTATION_TYPE.SET_IS_BONUS_SELECTED, value);
            dispatch('calculatePayout');
        },

        setSelectionAddError({ commit }, error: IBetSlipError | null) {
            commit(MUTATION_TYPE.SET_SELECTION_ADD_ERROR, error);
        },

        async recreateSelections(context, ticket: RecreatableTicket) {
            context.dispatch('setIsRecreatingTicket', true);

            await context.dispatch('clearSelections');
            await createSelections(context, {
                ticket,
                sourceScreen: ticket.isTicketOwner
                    ? SelectionSourceScreen.TicketDetailsTicketRecreate
                    : SelectionSourceScreen.TicketDetailsTicketCopy,
            });
            context.dispatch('setIsRecreatingTicket', false);
        },

        async recreateTicket(context, ticket: RecreatableTicket) {
            const { dispatch } = context;
            context.dispatch('setIsRecreatingTicket', true);

            await context.dispatch('clearSelections');
            context.commit(MUTATION_TYPE.SET_PURCHASE_TYPE, BetSlipPurchaseType.online);
            await dispatch('setStake', ticket.payment.total);
            if (ticket.system) {
                await dispatch('setType', BetSlipType.system);
                await dispatch('setSystems', { systems: [...ticket.system.selected] });
            } else {
                await dispatch('setType', BetSlipType.simple);
            }

            await createSelections(context, {
                ticket,
                sourceScreen: SelectionSourceScreen.TicketDetailsTicketRecreate,
            });

            context.dispatch('calculatePayout');
            context.dispatch('checkBetSlipValidation');
            context.dispatch('setIsRecreatingTicket', false);
        },

        setIsRecreatingTicket(context: BetSlipContext, value: boolean) {
            context.commit(MUTATION_TYPE.SET_IS_RECREATING_TICKET, value);
        },

        async prepareTicket(context, payload: { isResubmit?: boolean, geoLocation: string | null, requestUuid?: string } = { isResubmit: false, geoLocation: null }) {
            const { dispatch, getters, state, rootState } = context;
            dispatch('validateSelections', true);
            dispatch('validateBetSlipErrors', true);
            if (
                getters.betSlipErrors.length > 0
                || getters.errors.length > 0
                || !_isEmpty(getters.selectionErrors)
                || getters.bonusError?.type === BonusErrorType.UserBonusBalance
            ) {
                return;
            }

            const ticketBonus = rootState.ui.user.bonuses.activeBonusEligibility;

            const betSlip = {
                selections: getters.selections,
                stake: getters.stake.gross,
                purchaseType: state.purchaseType,
                type: getters.type,
                autoUpdateChanges: getters.autoUpdateChanges,
                selectedSystems: getters.selectedSystems,
            };

            const paymentBonusType = getPaymentBonusType({
                activeBonus: ticketBonus,
                stake: betSlip.stake,
                userBalance: rootState.data.authentication.user?.balance?.realMoney.total || 0,
                isBonusSelected: getters.isBonusSelected,
            });

            const ticketDto = await transformSportBetSlipToTicket({
                ...betSlip,
                paymentBonusType,
                geoLocation: payload.geoLocation ?? undefined,
            });

            return {
                isResubmit: payload.isResubmit,
                ticketDto,
                ticketType: TicketType.Sport,
                bonus: ticketBonus,
                requestUuid: payload.requestUuid,
            };
        },
        async checkBetSlipValidation(context) {
            await context.dispatch('data/tickets/clearAllErrors', null, { root: true });
            await context.dispatch('validateSelections');
            await context.dispatch('validateBetSlipErrors');
        },
        _subscribeSelectionForUpdates(context: BetSlipContext, selection: ISelection) {
            // For Bet Builder, we need to listen to the custom BB endpoints instead
            if (selection.sourceType === SelectionSourceType.BetBuilder) {
                context.dispatch(
                    'data/betBuilder/subscribeToBetslipBetBuilderOddUpdates',
                    { eventId: selection.getEventId(), oddUuid: selection.getOddUuid(), oddMarketName: selection.name, bbOddUuids: selection.odd?.legs?.map(({ oddUuid }) => oddUuid) },
                    { root: true },
                );
            } else {
                matchSubscriptions.add(context, selection.getEventId());
            }
        },
        _unsubscribeSelectionFromUpdates(context: BetSlipContext, selection: ISelection) {
            // See subscribe helper for explanation
            if (selection.sourceType === SelectionSourceType.BetBuilder) {
                context.dispatch(
                    'data/betBuilder/unsubscribeFromBetslipBetBuilderOddUpdates',
                    selection.getEventId(),
                    { root: true },
                );
            } else {
                matchSubscriptions.remove(selection.getEventId());
            }
        },
        sendBetslipAddResultAnalytic(context, { selection, isSuccess, sourceScreen, isSearchActive, isFavourited }:
            {
                selection: ISelection,
                isSuccess: boolean,
                sourceScreen: SelectionSourceScreen,
                isSearchActive?: boolean,
                isFavourited?: boolean
            },
        ) {
            const { event, odd } = selection;
            const isOnOfferEventPage = context.rootGetters['navigation/isOnOfferEventPage'];
            const isOnCompetitionOutrightsPage = context.rootGetters['navigation/isOnCompetitionOutrightsPage'];
            const hasMarketGroups = isOnOfferEventPage || isOnCompetitionOutrightsPage;
            const marketGroupFilterId = context.rootState.ui.sportOffer.markets.selectedMarketGroupId ?? ALL_MARKETS_GROUP_ID;
            AnalyticsSDK.getInstance().log('betslip_add_result', {
                result: isSuccess ? BetslipResult.SUCCESSFUL : BetslipResult.FAILED,
                event_status: event?.isOngoing() ? Status.LIVE : Status.PREMATCH,
                sport_id: getParamWithPrefix(Prefix.AXILIS, ParamTypes.SPORT, event?.sportId),
                tournament_id: getParamWithPrefix(Prefix.AXILIS, ParamTypes.TOURNAMENT, event?.tournamentId),
                category_id: getParamWithPrefix(Prefix.AXILIS, ParamTypes.CATEGORY, event?.categoryId),
                event_id: getParamWithPrefix(Prefix.AXILIS, ParamTypes.MATCH, event?.id),
                market_group_id: odd?.marketId ?? -1,
                source_screen: SelectionSourceScreenMap[sourceScreen],
                price: odd?.value?.toString() ?? '',
                odd_uuid: odd?.uuid ?? '',
                ...(hasMarketGroups && {
                    market_group_filter_id: marketGroupFilterId,
                    is_search_active: isSearchActive,
                    is_favourited: isFavourited,
                }),
            });
        },
        async toggleSelectionBase(context, payload: { selection: ISelection; }) {
            const isMultiMarketSelectEnabled = await getMultiMarketSelectEnabledFlag();
            if (isMultiMarketSelectEnabled) {
                return helpers.handleSelectionToggle(context, payload);
            } else {
                return helpers.handleSelectionToggleLegacy(context, payload);
            }
        },
        updateSelection({ commit, state }, { selection }: { selection: ISelection }) {
            const existingSelectionIndex = findSelectionIndex(
                state.selections,
                selection,
            );
            if (existingSelectionIndex !== -1) {
                commit(MUTATION_TYPE.UPDATE_SELECTION, { selection, index: existingSelectionIndex });
            }
        },
        markSelectionAsFinished({ commit, state }, { selection }: { selection: SportSelection }) {
            const existingSelectionIndex = findSelectionIndex(
                state.selections,
                selection,
            );

            if (existingSelectionIndex !== -1) {
                const updatedSelection = selection.duplicate();
                updatedSelection.odd = oddAsFinished(updatedSelection.odd!);
                commit(MUTATION_TYPE.UPDATE_SELECTION, { selection: updatedSelection, index: existingSelectionIndex });
            }
        },
        _removeSelection({ commit, state }, { selection }: { selection: ISelection }) {
            const existingSelectionIndex = findSelectionIndex(
                state.selections,
                selection,
            );
            if (existingSelectionIndex !== -1) {
                commit(MUTATION_TYPE.REMOVE_SELECTION, { selection, index: existingSelectionIndex });
            }
            const largestSystem = Math.max(...state.selectedSystems);
            if (largestSystem > state.selections.filter((s) => !s.isFixed).length) {
                commit(MUTATION_TYPE.REMOVE_SELECTED_SYSTEM, largestSystem);
            }
        },

        setSelectionIsFixed(context, payload: { selection: ISelection; isFixed: boolean }) {
            if (!payload.isFixed && helpers.getNumberOfSelectionsEligibleForSystems(context) >= maxSystemsCount) {
                return;
            }
            context.commit(MUTATION_TYPE.SET_SELECTION_IS_FIXED, payload);
        },
        // used only in duplicate and deserialize. Do not use elsewhere
        setSelections({ commit }, { selections }: { selections: ISelection[] }) {
            commit(MUTATION_TYPE.SET_SELECTIONS, selections);
        },
        setSystems({ commit }, { systems }: { systems: number[] }) {
            commit(MUTATION_TYPE.SET_SELECTED_SYSTEMS, systems);
        },
        clearSelections(context) {
            context.commit(MUTATION_TYPE.CLEAR_SELECTIONS);
        },
    };

    return actions;
}

function oddAsFinished(odd: BetSlipOdd): BetSlipOdd {
    return {
        id: odd.id,
        value: odd.value,
        name: odd.name,
        isLocked: odd.isLocked,
        marketId: odd.marketId,
        marketName: odd.marketName,
        marketUuid: odd.marketUuid,
        description: odd.description,
        specialBetValue: odd.specialBetValue,
        isFinished: true,
        isShownOnOffer: odd.isShownOnOffer,
        isBonusable: odd.isBonusable,
        isInPromotion: odd.isInPromotion,
        tags: odd.tags,
        uuid: odd.uuid,
        isSuperbet: odd.isSuperbet,
        sourceType: odd.sourceType,
    };
}

function getBetBuilderConflictAnalytics(context: BetSlipContext, selection: ISelection) {
    const isNewSelectionBetBuilder = selection.sourceType === SelectionSourceType.BetBuilder;
    const payload = {
        eventId: selection.getEventId(),
        isNewSelectionBetBuilder,
    };

    return {
        conflictOccurred: () => context.dispatch(
            'data/betBuilder/analytics/conflictOccurred',
            payload,
            { root: true },
        ),
        conflictReplaced: () => context.dispatch(
            'data/betBuilder/analytics/conflictReplaced',
            payload,
            { root: true },
        ),
        conflictCancelled: () => context.dispatch(
            'data/betBuilder/analytics/conflictCancelled',
            payload,
            { root: true },
        ),
    };
}

function recordBetBuilderBetRemoved(context: BetSlipContext, selection: ISelection) {
    const payload = {
        eventId: selection.getEventId(),
        marketName: selection.name,
        oddValue: selection.getOddValue() ?? 0,
    };

    context.dispatch(
        'data/betBuilder/analytics/betslipEventRemoved',
        payload,
        { root: true },
    );
}

type BetUrlParams = {
    mi?: number;
    oi?: number;
    ouuid?: string;
    osbv?: string;
    fix?: string;
};

type CopiedTicketUrlParams = {
    id: string;
    userId: string;
    ticketCopySource: string;
    videoId: string;
};

type CopiedSelectionUrlParams = {
    analysisId?: string;
    analysisSource?: string;
    userId?: string;
    ticketId: string;
    event?: {
        eventId: string;
    };
    odd?: {
        oddUuid: string;
    };
};

type CreateSelectionData = {
    type?: SelectionType;
    eventId: ISportlikeEvent['id'];
    oddId: number;
    specialBetValue?: string;
    isFixed?: boolean;
    uuid?: string;
    originalOdd?: RecreatableTicket['events'][0]['odd'];
    originalMarketId?: number;
    sourceScreen?: SelectionSourceScreen;
    index?: number;
    eventComponents?: RecreatableTicket['events'][0]['eventComponents'];
};

type BetSlipSelection = {
    eventId: number;
    oddId: number;
    specialBetValue?: string;
    type?: SelectionType;
    uuid?: string;
    sourceScreen?: SelectionSourceScreen;
    index?: number;
    isSearchActive?: boolean;
    isFixed?: boolean;
    isFavourited?: boolean;
    eventComponents?: RecreatableTicket['events'][0]['eventComponents'];
};

type ToggleSelectionResult = {
    isSuccess: boolean;
    addResult?: SelectionAddResult;
};
